import {
  DELETE_WORLD_CANCEL,
  DELETE_WORLD_GOES,
  deleteWorldKeeps,
  DELETE_WORLD_UNDO,
  deleteWorldHeritage,
  deleteWorldTitle,
  deleteWorldTypeName,
  REASON_WAIT,
} from '../game/copy.ts';
import { Button } from '../ui/Button.tsx';
import { Sheet } from '../ui/Sheet.tsx';

export interface DeleteWorldProps {
  /** The world's name, which is also what has to be typed. */
  name: string;
  /** The world's Heritage, lost with it (D221). */
  heritage?: number;
  /** What goes with the world. An idle world's list differs from an old-game world's (D337). */
  loses: readonly string[];
  typed: string;
  onType: (text: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  /** The request is out, so Delete cannot be pressed twice. */
  busy?: boolean;
  /** Why the last try did not delete it, when it did not. */
  error?: string | null;
  /**
   * D363: the Delete button's label, the name box's accessible name, and why it is greyed.
   * The app's own words.
   */
  confirmLabel: string;
  inputLabel: string;
  needsNameReason: string;
}

/** D221: Delete is live only once the name is typed exactly, case and spaces included. */
export function deleteWorldReady(name: string, typed: string): boolean {
  return typed === name;
}

/**
 * D221: the confirm sheet's contents. Kept apart from the frame so the bench can walk it,
 * since the frame binds Escape with a hook and a hook needs a render to run in.
 */
export function DeleteWorldBody({
  name,
  heritage = 0,
  loses,
  typed,
  onType,
  onConfirm,
  onCancel,
  busy = false,
  error = null,
  confirmLabel,
  inputLabel,
  needsNameReason,
}: DeleteWorldProps) {
  const ready = deleteWorldReady(name, typed);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ font: '600 14px/1.4 var(--font-ui)', color: 'var(--red)' }}>
        {DELETE_WORLD_UNDO}
      </div>
      <div>
        <div style={{ font: '400 14px/1.4 var(--font-ui)' }}>{DELETE_WORLD_GOES}</div>
        <ul style={{ margin: '4px 0 0', paddingLeft: 20, font: '400 14px/1.5 var(--font-ui)' }}>
          {loses.map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>{deleteWorldHeritage(heritage)}</li>
        </ul>
      </div>
      <div style={{ font: '400 13px/1.4 var(--font-ui)', color: 'var(--ink-2)' }}>
        {deleteWorldKeeps()}
      </div>
      <label style={{ display: 'grid', gap: 4, font: '400 14px/1.4 var(--font-ui)' }}>
        {deleteWorldTypeName(name)}
        <input
          value={typed}
          data-testid="delete-world-name"
          aria-label={inputLabel}
          autoComplete="off"
          spellcheck={false}
          onInput={(e) => onType((e.currentTarget as HTMLInputElement).value)}
          style={{
            font: '400 var(--fs-name)/1.4 var(--font-ui)',
            padding: '0 12px',
            height: 44,
            boxSizing: 'border-box',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface)',
            color: 'var(--ink)',
          }}
        />
      </label>
      {error && (
        <div style={{ font: '400 13px/1.4 var(--font-ui)', color: 'var(--red)' }}>{error}</div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="secondary" onClick={onCancel}>
          {DELETE_WORLD_CANCEL}
        </Button>
        <Button
          variant="danger"
          disabled={!ready || busy}
          {...(ready ? { explain: REASON_WAIT } : { reason: needsNameReason })}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

/** D221: the confirm sheet, over the Worlds card. */
export function DeleteWorldSheet(props: DeleteWorldProps) {
  return (
    <Sheet
      align="bottom"
      title={deleteWorldTitle(props.name)}
      testId="delete-world-sheet"
      onClose={props.onCancel}
      zIndex={30}
    >
      <DeleteWorldBody {...props} />
    </Sheet>
  );
}
